import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { motion } from "framer-motion";
import whyChooseUs from "../../static/media/Home/WhyChooseUs.webp";
import why1 from "../../static/media/Home/WhyChooseUs/why1.webp";
import why2 from "../../static/media/Home/WhyChooseUs/why2.webp";
import why3 from "../../static/media/Home/WhyChooseUs/why3.webp";
import why4 from "../../static/media/Home/WhyChooseUs/why4.webp";
import why5 from "../../static/media/Home/WhyChooseUs/why5.webp";
import why6 from "../../static/media/Home/WhyChooseUs/why6.webp";

const PRIMARY = "#06BBC7";

const reasons = [
  {
    title: "Tratamientos con respaldo profesional",
    image: why1,
    position: {
      left: 10,
      top: 18,
    },
  },
  {
    title: "Ambiente cálido y relajante",
    image: why2,
    position: {
      left: -42,
      top: "50%",
      mt: "-52px",
    },
  },
  {
    title: "Enfoque personalizado",
    image: why3,
    position: {
      left: 10,
      bottom: 18,
    },
  },
  {
    title: "Tecnología estética de vanguardia",
    image: why4,
    position: {
      right: 10,
      top: 18,
    },
  },
  {
    title: "Resultados reales y sostenibles",
    image: why5,
    position: {
      right: -42,
      top: "50%",
      mt: "-52px",
    },
  },
  {
    title: "Belleza consciente",
    image: why6,
    position: {
      right: 10,
      bottom: 18,
    },
  },
];

const cardAnimation = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  whileHover: {
    y: -6,
    boxShadow: "0 20px 44px rgba(6, 187, 199, 0.18)",
  },
  transition: { duration: 0.42, ease: "easeOut" },
  viewport: { once: true, amount: 0.25 },
};

const ReasonCard = ({ title, image, position, mobileCenter = false }) => (
  <Box
    component={motion.div}
    initial={cardAnimation.initial}
    whileInView={cardAnimation.whileInView}
    whileHover={cardAnimation.whileHover}
    transition={cardAnimation.transition}
    viewport={cardAnimation.viewport}
    sx={{
      width: "100%",
      maxWidth: {
        xs: mobileCenter ? 178 : "90%",
        sm: mobileCenter ? 210 : "100%",
        md: 315,
      },
      mx: "auto",
      position: { xs: "relative", md: "absolute" },
      left: { xs: "auto", md: position.left },
      right: { xs: "auto", md: position.right },
      top: { xs: "auto", md: position.top },
      bottom: { xs: "auto", md: position.bottom },
      mt: { xs: 0, md: position.mt || 0 },
      bgcolor: "#ffffff",
      border: "1px solid rgba(6, 187, 199, 0.18)",
      borderRadius: { xs: "22px", md: "28px" },
      px: { xs: 1.15, sm: 1.4, md: 1.25 },
      py: { xs: 1.3, md: 1.15 },
      boxShadow: "0 14px 36px rgba(15, 23, 42, 0.07)",
      minHeight: { xs: 120, sm: 150, md: 90 },
      display: "flex",
      flexDirection: { xs: "column", md: "row" },
      alignItems: "center",
      justifyContent: { xs: "flex-start", md: "flex-start" },
      gap: { xs: 1.1, md: 1.45 },
      cursor: "default",
      overflow: "hidden",
      zIndex: 3,
    }}
  >
    <Box
      component="img"
      src={image}
      alt={title}
      loading="lazy"
      sx={{
        width: { xs: 72, sm: 78, md: 86 },
        height: { xs: 72, sm: 78, md: 86 },
        borderRadius: "50%",
        objectFit: "cover",
        flexShrink: 0,
        border: `2px solid ${PRIMARY}`,
        bgcolor: "#ffffff",
      }}
    />

    <Typography
      sx={{
        color: "#1f2937",
        fontSize: { xs: "0.82rem", sm: "0.9rem", md: "0.98rem" },
        fontWeight: 700,
        lineHeight: { xs: 1.18, md: 1.22 },
        textAlign: { xs: "center", md: "left" },
      }}
    >
      {title}
    </Typography>
  </Box>
);

const MobileCardsGroup = ({ first, second, third, reverse = false }) => (
  <Box
    sx={{
      display: { xs: "flex", md: "none" },
      flexDirection: "column",
      alignItems: "center",
      gap: 1.6,
      width: "100%",
    }}
  >
    {!reverse && (
      <>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: { xs: 1.4, sm: 1.8 },
            width: "100%",
          }}
        >
          {[first, second].map((item) => (
            <ReasonCard
              key={item.title}
              title={item.title}
              image={item.image}
              position={item.position}
            />
          ))}
        </Box>

        <ReasonCard
          title={third.title}
          image={third.image}
          position={third.position}
          mobileCenter
        />
      </>
    )}

    {reverse && (
      <>
        <ReasonCard
          title={first.title}
          image={first.image}
          position={first.position}
          mobileCenter
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: { xs: 1.4, sm: 1.8 },
            width: "100%",
          }}
        >
          {[second, third].map((item) => (
            <ReasonCard
              key={item.title}
              title={item.title}
              image={item.image}
              position={item.position}
            />
          ))}
        </Box>
      </>
    )}
  </Box>
);

const WhyChooseUs = () => {
  const topMobileCards = reasons.slice(0, 3);
  const bottomMobileCards = reasons.slice(3);

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 6.5, md: 9 },
        background: `
          radial-gradient(circle at 12% 22%, rgba(6, 187, 199, 0.065) 0%, rgba(6, 187, 199, 0.025) 26%, transparent 46%),
          radial-gradient(circle at 88% 28%, rgba(6, 187, 199, 0.06) 0%, rgba(6, 187, 199, 0.022) 24%, transparent 45%),
          radial-gradient(circle at 50% 92%, rgba(6, 187, 199, 0.055) 0%, rgba(6, 187, 199, 0.02) 25%, transparent 48%),
          linear-gradient(180deg, #ffffff 0%, rgba(6, 187, 199, 0.018) 100%)
        `,
        overflow: "hidden",
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ textAlign: "center", mb: { xs: 4.5, md: 6 } }}>
          <Typography
            sx={{
              color: PRIMARY,
              fontSize: { xs: "0.78rem", md: "0.9rem" },
              fontWeight: 700,
              letterSpacing: "0.28em",
              textTransform: "uppercase",
              mb: 1.4,
            }}
          >
            Nuestro compromiso contigo
          </Typography>

          <Typography
            component="h2"
            sx={{
              fontFamily: "'Playfair Display', serif",
              fontSize: { xs: "2.1rem", sm: "2.8rem", md: "3.5rem" },
              fontWeight: 500,
              color: "#1d2530",
              letterSpacing: "-0.04em",
              lineHeight: 1.05,
              mb: 2,
            }}
          >
            ¿Por qué elegirnos?
          </Typography>

          <Typography
            sx={{
              maxWidth: 720,
              mx: "auto",
              color: "#374151",
              fontSize: { xs: "0.98rem", md: "1.08rem" },
              fontWeight: 400,
              lineHeight: 1.75,
              px: { xs: 1, sm: 0 },
            }}
          >
            Creamos experiencias de cuidado estético con atención cercana,
            delicadeza y un enfoque pensado especialmente para ti.
          </Typography>
        </Box>

        <Box
          sx={{
            maxWidth: 1160,
            mx: "auto",
            position: "relative",
            minHeight: { xs: "auto", md: 560 },
            display: { xs: "flex", md: "block" },
            flexDirection: { xs: "column", md: "initial" },
            alignItems: { xs: "center", md: "initial" },
            gap: { xs: 2.2, md: 0 },
          }}
        >
          <Box sx={{ display: { xs: "none", md: "contents" } }}>
            {reasons.map((item) => (
              <ReasonCard
                key={item.title}
                title={item.title}
                image={item.image}
                position={item.position}
              />
            ))}
          </Box>

          <MobileCardsGroup
            first={topMobileCards[0]}
            second={topMobileCards[1]}
            third={topMobileCards[2]}
          />

          <Box
            sx={{
              width: { xs: "100%", md: 430 },
              maxWidth: { xs: 360, sm: 420, md: 430 },
              mx: "auto",
              my: { xs: 1.2, md: 0 },
              position: { xs: "relative", md: "absolute" },
              left: { md: "50%" },
              top: { md: "50%" },
              transform: { md: "translate(-50%, -50%)" },
              zIndex: 1,
            }}
          >
            <Box
              component="img"
              src={whyChooseUs}
              alt="Bienestar y estética corporal"
              loading="lazy"
              sx={{
                width: "100%",
                height: "auto",
                display: "block",
                borderRadius: { xs: "42px", md: "64px" },
              }}
            />
          </Box>

          <MobileCardsGroup
            first={bottomMobileCards[0]}
            second={bottomMobileCards[1]}
            third={bottomMobileCards[2]}
            reverse
          />
        </Box>
      </Container>
    </Box>
  );
};

export default WhyChooseUs;